<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { onMounted, ref, watch } from "vue";
import { PurchasedProduct, Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes.ts";
import { fetchData } from "../../utils/ProductViewUtils.ts";
import { ProfileAdministrationAPI } from "../../api";
import { useToast } from "primevue/usetoast";

const { t } = useI18n();

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    account: {
        type: Object as () => Trainer,
        required: true,
    },
});

const toast = useToast();
const loading = ref(false);
const visible = ref(props.visibleToggle);
const purchasedProducts = ref<TranslatedProduct[]>([]);
const selectedProduct = ref<TranslatedProduct>();
const selectedQuantity = ref<number>(0);
const receivingAccount = ref<Trainer>(props.account);

const emit = defineEmits(["update:visible"]);

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    },
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    },
);

/**
 * Resolves a receiving account entered by e-mail-address: an existing sub-account of the distributor,
 * or a new trainer record for a Keycloak user who never logged in (EP-3513). The backend sets that
 * record up as a sub-account of the distributor; the client no longer creates trainer records for
 * other people (RDT-445).
 */
async function findOrCreateAccount(account: Trainer): Promise<Trainer | undefined> {
    try {
        return await ProfileAdministrationAPI.findOrCreateSubAccount(props.trainer.id, account.email.trim());
    } catch (error) {
        console.error("Error resolving account:", error);
        return undefined;
    }
}

/**
 * Transfers the selected product to the receiving account.
 * props.account.id === "0" means that the account was entered by e-mail-address and still has to be resolved.
 */
async function transferProducts() {
    if (props.trainer && props.account && selectedProduct.value && selectedQuantity.value > 0) {
        loading.value = true;
        if (props.account.id === "0") {
            const account = await findOrCreateAccount(props.account);
            if (!account) {
                toastTransferNoAccount();
                loading.value = false;
                return;
            }
            receivingAccount.value = account;
        } else {
            receivingAccount.value = props.account;
        }

        if (props.trainer.distributor.id !== receivingAccount.value.distributor.id) {
            toastTransferError(true, "");
            loading.value = false;
            return;
        }

        const product: PurchasedProduct = {
            id: 0,
            product: selectedProduct.value.productNo,
            quantity: selectedQuantity.value,
            trainer: props.trainer.id,
        };

        try {
            await ProfileAdministrationAPI.transferProducts(props.trainer.id, receivingAccount.value.id, product);
            toastTransferSucess(
                selectedProduct.value.productName,
                selectedQuantity.value,
                `${receivingAccount.value.firstname} ${receivingAccount.value.lastname}`,
            );
            visible.value = false;
        } catch (error) {
            console.error("Error transferring products:", error);
            if (props.account.id !== "0") {
                toastTransferError(false, `${receivingAccount.value.firstname} ${receivingAccount.value.lastname}`);
            }
        } finally {
            loading.value = false;
        }
    }
}

function toastTransferSucess(product: string, quantity: number, recipient: string) {
    toast.add({
        severity: "success",
        summary: t("header.accountSettings.accountLinks.transfer.headingSuccess"),
        detail: t("header.accountSettings.accountLinks.transfer.message", {
            product: product,
            quantity: quantity,
            recipient: recipient,
        }),
        life: 10000,
    });
}

function toastTransferError(distributorError: boolean, recipient: string) {
    toast.add({
        severity: "error",
        summary: t("header.accountSettings.accountLinks.transfer.errorHeading"),
        detail: distributorError
            ? t("header.accountSettings.accountLinks.transfer.errorDistributor")
            : t("header.accountSettings.accountLinks.transfer.errorMessage", { recipient: recipient }),
        life: 10000,
    });
}

function toastTransferNoAccount() {
    toast.add({
        severity: "error",
        summary: t("header.accountSettings.accountLinks.transfer.errorHeading"),
        detail: t("header.accountSettings.accountLinks.transfer.errorNoAccount"),
        life: 10000,
    });
}

onMounted(async () => {
    purchasedProducts.value = (await fetchData()).purchased;
    receivingAccount.value = props.account;
});
</script>
<template>
    <Toast />
    <Dialog
        v-model:visible="visible"
        modal
        :header="t('header.accountSettings.accountLinks.profiles')"
        position="center"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <div class="form-container">
            <div class="dropdown-container">
                <Select
                    :id="`transfer-dialog_dropdown-product-${account.id}`"
                    v-model="selectedProduct"
                    :options="purchasedProducts"
                    filter
                    option-label="productName"
                    :placeholder="t('fields.search')"
                    class="w-full md:w-56"
                />
                <!-- Renders only once a product is selected, which is also when the
                     quantity input's max stops being 0 — tests wait on it before typing. -->
                <label
                    v-if="selectedProduct"
                    :id="`transfer-dialog_available-${account.id}`"
                    :for="`transfer-dialog_input-quantity-${account.id}`"
                    class="quantity-label"
                >
                    {{ t("common.profiles.availableProfiles") }}: <strong>{{ selectedProduct?.quantity ?? 0 }}</strong>
                </label>
            </div>
            <div class="input-number-container">
                <InputNumber
                    v-model="selectedQuantity"
                    :input-id="`transfer-dialog_input-quantity-${account.id}`"
                    show-buttons
                    button-layout="horizontal"
                    decrement-button-class="p-button-secondary"
                    increment-button-class="p-button-secondary"
                    increment-button-icon="pi pi-plus"
                    decrement-button-icon="pi pi-minus"
                    mode="decimal"
                    :min="0"
                    :max="selectedProduct?.quantity ?? 0"
                />
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 mt-8">
                <Button
                    severity="secondary"
                    :label="t('buttons.cancel')"
                    icon="pi pi-times"
                    @click="visible = false"
                    size="small"
                    text
                />
                <Button
                    severity="secondary"
                    :id="`transfer-dialog_btn-submit-${account.id}`"
                    @click="transferProducts"
                    :label="t('buttons.transfer')"
                    :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-file-export'"
                    size="small"
                    autofocus
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
/* Align items at the start of the container */
.form-container {
    margin-top: 1rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.dropdown-container {
    display: flex;
    flex-direction: column;
}

.quantity-label {
    font-size: 0.875rem;
    margin-top: 0.5rem;
}

/* Align InputNumber vertically with Select */
.input-number-container {
    display: flex;
    align-items: center;
    height: auto;
}
</style>
